import UsuarioStore from '@/store/UsuarioStore';
import chunk from 'lodash/chunk';
import { runInAction } from 'mobx';
import moment from 'moment';
import MinutaApi from '../api/MinutaApi';
import MinutaWebdavApi from '../api/MinutaWebdavApi';
import ProcessoApi from '../api/ProcessoApi';
import { IMinutaAssinada, IMinutaAssinadaPage, IModelo, Minuta, Processo, TipoMinuta } from '../modelo';
import { EmentaDecisao } from '../modelo/EmentaDecisao';
import { HistoricoMinutaDto } from '../modelo/HistoricoMinuta';
import { HistoricoSituacaoMinutaDto } from '../modelo/HistoricoSituacaoMinuta';
import { IMinutaEmEdicaoExterna } from "../modelo/MinutaEmEdicaoExterna";
import { VersaoMinutaDto } from '../modelo/dto/VersaoMinutaDto';
import MensagemStore from '../store/MensagemStore';
import ProcessoStore from '../store/ProcessoStore';
import { apresentarApiErroPadrao } from '../utils/ApiErroPadraoUtils';
import MovimentoService from './MovimentoService';
import { MapeadorMinuta } from './mapeadores/MapeadorMinuta';
import MinutaUtils from '../utils/MinutaUtils';

class MinutaService {
    upgradeVoto(numeroMinuta: number) {
        MinutaApi.upgradeVoto(numeroMinuta);
    }

    private mapeadorMinuta = new MapeadorMinuta();

    public async recuperarMinuta(idProcesso: number, tipoMinuta: number, conteudo, numeroMinuta?: number): Promise<Minuta> {
        const minutaDto = await MinutaApi.recuperarMinuta(idProcesso, tipoMinuta, conteudo, numeroMinuta);
        return Minuta.criar(this.mapeadorMinuta.toMinuta(minutaDto));
    }

    public async recuperarMinutaExterna(idProcesso: number, numeroMinuta: number, conteudo): Promise<Minuta> {
        const minutaDto = await MinutaApi.recuperarMinutaExterna(idProcesso, numeroMinuta, conteudo);
        return Minuta.criar(this.mapeadorMinuta.toMinuta(minutaDto));
    }

    public async getTiposMinuta() {
        return await MinutaApi.getTiposMinuta();
    }

    public async getSituacoesMinuta() {
        const situacoes = await MinutaApi.getSituacoesMinuta();
        return situacoes;
    }

    public async getSituacoesPeticao() {
        const situacoes = await MinutaApi.getSituacoesPeticao();
        return situacoes;
    }

    async getTiposMinutaProcesso(processo, parametroTiposMinuta) {
        let resultTiposMinuta = parametroTiposMinuta;
        if (!parametroTiposMinuta) {
            resultTiposMinuta = await MinutaApi.getTiposMinuta();
        }

        const resultTiposExistentes = await MinutaApi.getTiposMinutaProcesso(processo.identificadorProcesso);
        const existentes = resultTiposMinuta.length > 0 ? resultTiposMinuta.filter(tipo => {
            const existentes = resultTiposExistentes.headers['x-tipos'];
            return (existentes !== undefined && existentes.search(tipo.codigo) !== -1);
        }) : [];

        return existentes;
    }

    async salvarMinutaDados(processo, conteudoHTML, tipoMinuta, login, siglaGabinete, publicavel, categoria, labelProcesso, tipoDocumentoMinuta = 'docx') {
        return await MinutaApi.salvarMinutaDados(processo, conteudoHTML, tipoMinuta, login, siglaGabinete, publicavel, categoria, labelProcesso, tipoDocumentoMinuta);
    }

    async alterarSituacaoMinuta(minuta: Minuta, login) {
        const siglaGabinete = minuta.siglaOrgaoJulgador;
        const idProcesso = minuta.idProcesso;
        return await MinutaApi.alterarSituacaoMinuta(minuta, login, siglaGabinete, idProcesso);
    }
    async alterarSituacaoMinutaEmLote(minutas: Minuta[], codSituacao: number) {
        const numMinutas = minutas.map(minuta => minuta.numero);
        return await MinutaApi.alterarSituacaoMinutaEmLote(numMinutas, codSituacao, UsuarioStore.login);
    }

    public async salvarMinutaAPartirDeModelo(
        processo: Processo,
        minuta: Minuta,
        modelo: IModelo,
        login: string,
        edicaoExterna: boolean = false,
        edicaoAguia: boolean = false,
        funcaoSignatario?: string
    ) {
        await MinutaWebdavApi.criarAPartirDeModelo(processo, minuta, modelo, login, edicaoExterna, edicaoAguia, funcaoSignatario);

        await this.aplicarAposPautaSeNecessario(processo, minuta, modelo, login);
    }

    public async aplicarAposPautaSeNecessario(
        processo: Processo,
        minuta: Minuta,
        modelo: IModelo,
        login: string
    ) {

        const tipo = minuta?.tipo?.codigo;
        const ehDespachoOuDecisao = tipo === TipoMinuta.DESPACHO || tipo === TipoMinuta.DECISAO;
        if (!ehDespachoOuDecisao || !modelo?.incluirAposPauta) {
            return;
        }

        if (!processo?.identificadorProcesso || !UsuarioStore.gabinete) {
            return;
        }

        await MinutaApi.salvarDadosRevisaoPauta(
            processo.identificadorProcesso,
            minuta,
            login,
            UsuarioStore.gabinete,
            { autorizacaoPauta: true }
        );
        minuta.autorizacaoPauta = true;

        // Garante que o texto também esteja no conteúdo da minuta quando criado por modelo/lote.
        await this.inserirTrechoAposPautaNoConteudoSeNecessario(processo, minuta, login);
    }

    public aplicarAposPautaNoHtml(conteudoHtml: string): string {
        if (!conteudoHtml) {
            return conteudoHtml;
        }

        const html = document.createElement('div');
        html.innerHTML = conteudoHtml;

        const normalizar = (texto: string) => (texto || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[.,;:!?()"']/g, '')
            .replace(/\s+/g, ' ')
            .trim();

        let elementosTexto = Array.from(html.querySelectorAll('p, div, span')) as HTMLElement[];

        const elementosAposPauta = elementosTexto.filter((el) => {
            const texto = normalizar(el.textContent || '');
            return texto === 'apos a pauta' || /\bapos\s*,?\s*a\s+pauta\b|\bapos\s*,?\s+pauta\b/.test(texto);
        });

        elementosAposPauta.forEach((el) => {
            el.parentNode?.removeChild(el);
        });

        elementosTexto = Array.from(html.querySelectorAll('p, div, span')) as HTMLElement[];

        const candidatosPubliquese = elementosTexto
            .map((el) => ({
                el,
                texto: normalizar(el.textContent || '')
            }))
            .filter((item) => (
                item.texto.length > 0
                && item.texto.length <= 80
                && (item.texto === 'publique-se' || item.texto === 'publiquese' || /\bpublique\s*-?\s*se\b/.test(item.texto))
            ))
            .sort((a, b) => a.texto.length - b.texto.length);

        const alvoPubliquese = candidatosPubliquese[0]?.el;

        const assinatura = elementosTexto.find((el) => el.getAttribute('data-sv-topico') === 'assinatura');

        const textoAposPauta = 'Após, à Pauta.';
        const tagsDeBloco = ['P', 'DIV', 'LI'];

        const aplicarTextoMantendoEstrutura = (elemento: HTMLElement, novoTexto: string) => {
            const regexPubliquese = /publique\s*-?\s*se\.?/i;
            const walker = document.createTreeWalker(elemento, NodeFilter.SHOW_TEXT);

            let noAtual = walker.nextNode() as Text | null;
            while (noAtual) {
                const atual = noAtual.nodeValue || '';
                if (regexPubliquese.test(atual)) {
                    noAtual.nodeValue = atual.replace(regexPubliquese, novoTexto);
                    return;
                }
                noAtual = walker.nextNode() as Text | null;
            }

            elemento.textContent = novoTexto;
        };

        const obterBlocoDaLinha = (elemento: HTMLElement): HTMLElement => {
            let atual: HTMLElement | null = elemento;
            while (atual && atual !== html) {
                if (tagsDeBloco.includes(atual.tagName)) {
                    return atual;
                }
                atual = atual.parentElement;
            }
            return elemento;
        };

        let novoElemento: HTMLElement;
        let ancoraInsercao: HTMLElement | undefined;
        if (alvoPubliquese) {
            ancoraInsercao = obterBlocoDaLinha(alvoPubliquese);
            novoElemento = ancoraInsercao.cloneNode(true) as HTMLElement;
            aplicarTextoMantendoEstrutura(novoElemento, textoAposPauta);
        } else {
            novoElemento = document.createElement('p');
            novoElemento.setAttribute('data-sv-topico', 'corpo');
            novoElemento.setAttribute('class', 'corpo');
            novoElemento.textContent = textoAposPauta;
        }

        if (ancoraInsercao?.parentNode) {
            ancoraInsercao.parentNode.insertBefore(novoElemento, ancoraInsercao.nextSibling);
        } else if (assinatura?.parentNode) {
            assinatura.parentNode.insertBefore(novoElemento, assinatura);
        } else {
            html.appendChild(novoElemento);
        }

        return html.innerHTML;
    }

    private async inserirTrechoAposPautaNoConteudoSeNecessario(
        processo: Processo,
        minuta: Minuta,
        login: string
    ): Promise<void> {
        if (!processo?.identificadorProcesso || !minuta?.tipo?.codigo || !UsuarioStore.gabinete) {
            return;
        }

        const minutaAtual = await this.recuperarMinuta(
            processo.identificadorProcesso,
            minuta.tipo.codigo,
            true,
            minuta.numero
        );

        const conteudoOriginal = minutaAtual?.conteudo;
        if (!conteudoOriginal) {
            return;
        }

        const conteudoDecodificado = MinutaUtils.decodeMinuta(conteudoOriginal);

        // Modelo já pode incluir o texto; evita manipulação e save desnecessários
        if (/ap[oó]s\s*,?\s*[àa]\s*pauta/i.test(conteudoDecodificado)) {
            return;
        }

        const conteudoComAposPauta = this.aplicarAposPautaNoHtml(conteudoDecodificado);

        if (conteudoComAposPauta === conteudoDecodificado) {
            return;
        }

        await MinutaApi.salvarMinutaHtmlDocx(
            processo.identificadorProcesso,
            conteudoComAposPauta,
            minuta.tipo,
            login,
            UsuarioStore.gabinete,
            minuta.publicavel,
            undefined,
            processo.identificadorProcesso,
            'html'
        );

        await MinutaApi.salvarMinutaDados(
            processo.identificadorProcesso,
            conteudoComAposPauta,
            minuta.tipo,
            login,
            UsuarioStore.gabinete,
            minuta.publicavel,
            undefined,
            processo.identificadorProcesso,
            'html'
        );
    }

    public async salvarUnidadePublicacao(idProcesso: number, minuta: Minuta, unidadePublicacao: number) {
        const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso == idProcesso);
        if (processo.ehLegado) {
            if (!unidadePublicacao) {
                const unidadesPublicacao = await ProcessoApi.carregarUnidadesPublicacao(idProcesso);
                const unidadePadrao = unidadesPublicacao.find(unidade => unidade.padrao);
                unidadePublicacao = unidadePadrao.codigo;
            }
        }
        await MinutaApi.salvarUnidadePublicacao(idProcesso, minuta.tipo.codigo, unidadePublicacao);
    }

    public async salvarUnidadePublicacaoLote(idProcesso: number, unidadePublicacao: number) {
        const processo = ProcessoStore._processos.find(proc => proc.identificadorProcesso == idProcesso);
        if (processo.ehLegado) {
            if (!unidadePublicacao) {
                const unidadesPublicacao = await ProcessoApi.carregarUnidadesPublicacao(idProcesso);
                const unidadePadrao = unidadesPublicacao.find(unidade => unidade.padrao);
                unidadePublicacao = unidadePadrao.codigo;
            }
        }
        await MinutaApi.salvarUnidadePublicaoLote(idProcesso, unidadePublicacao)
    }

    async arquivarMinutas(numMinutas: number[]) {
        await MinutaApi.arquivarMinutas(numMinutas);
    }

    async gravarFuncaoMagistrado(idProcesso: number, idTipoMinuta: number, funcao: string) {
        return await MinutaApi.gravarFuncaoMagistrado(idProcesso, idTipoMinuta, funcao);
    }

    async converter64toHtml(conteudo64, retirarDivNFolhas, salvarMinuta?: boolean, minuta?: Minuta, login?: string) {
        return await MinutaApi.converter64toHtml(conteudo64, retirarDivNFolhas, salvarMinuta, minuta, login);
    }

    async listarMinutasAssinadas(
        siglaGabinete,
        pagina,
        tamanhoPagina,
        filtros?: {
            numeroProcesso?: string;
            numerosProcesso?: string[];
            tiposMinuta?: Array<string | number>;
            classes?: string[];
            dataInicioAssinatura?: string;
            dataFimAssinatura?: string;
        }
    ): Promise<IMinutaAssinada[] | IMinutaAssinadaPage> {
        return await MinutaApi.listarMinutasAssinadas(siglaGabinete, pagina, tamanhoPagina, filtros);
    }

    async listarMinutasComVotoVencido(siglaGabinete) {
        return await MinutaApi.listarMinutasComVotoVencido(siglaGabinete);
    }

    async verificarMinutaDocx(numeroMinuta: number): Promise<string> {
        const pendencia = await MinutaApi.verificaMinuta(numeroMinuta);
        const retorno = pendencia.filter(pend => pend != undefined && pend.length > 0);
        return retorno.length === 0 ? 'OK' : retorno.join('');
    }

    consultaLiberacaoVoto2 = async (idsProcesso: number[]): Promise<boolean[]> => {
        if (!idsProcesso || idsProcesso.length === 0) {
            return [];
        }

        let idsProcessoLiberados = {};
        const listaIds = chunk(idsProcesso, 900);
        const promises = listaIds.map(lista => MinutaApi.consultarLiberacaoConteudoVotoEmLote(lista));
        await Promise.all(promises).then(result => {
            result.forEach(r => idsProcessoLiberados = { ...idsProcessoLiberados, ...r });
        });
        return idsProcessoLiberados as any;
    }

    async carregarCategoriasDeMinutas(): Promise<void> {
        const numeros: number[] = [];

        const processosComMinutas = ProcessoStore._processos.filter((processo) => {
            return processo.minutas.filter((minuta) => {
                if (minuta.numero) {
                    numeros.push(minuta.numero);
                    return true;
                }
                return false;
            });
        });

        if (numeros.length === 0) {
            return;
        }

        const minutaComCategorias = await MinutaApi.recuperaCategoriaDasMinutas([...new Set(numeros)]);

        runInAction(() => {
            processosComMinutas.forEach((processo) => {
                processo.minutas.forEach((minuta) => {
                    minuta.categorias = minutaComCategorias[minuta.numero] || [];
                });
            });
        });
    }

    async carregarCategoriasDeMinutasPorIdsProcessos(idsProcessos: number[]): Promise<void> {
        const processos = ProcessoStore._processos
            .filter(proc => idsProcessos.includes(proc.identificadorProcesso))

        const numerosMinutas = processos.flatMap(proc => proc.minutas.map(minuta => minuta.numero))

        const minutaComCategorias = await MinutaApi.recuperaCategoriaDasMinutas(numerosMinutas);

        processos.forEach((processo) => {
            processo.minutas.forEach((minuta) => {
                runInAction(() => {
                    minuta.categorias = minutaComCategorias[minuta.numero] || [];
                });
            });
        });
    }

    async carregarMovimentosDeMinutas(): Promise<void> {
        runInAction(() => {
            ProcessoStore.carregandoMovimentosDeMinuta = true;
        });
        const processos: Processo[] = ProcessoStore._processos.filter((processo) => processo.minutas.length > 0);

        if (processos.length === 0) {
            return;
        }

        this.recuperarMovimentosMinutas(processos).then(movimentosMinutas => {
            processos.forEach((processo) => {
                processo.minutas.forEach((minuta) => {
                    runInAction(() => {
                        minuta.movimentosCNJ = [];
                        movimentosMinutas.forEach(listaMM => {
                            if (listaMM?.length > 0) {
                                const primeiro = listaMM[0]
                                if (primeiro?.numMinuta == minuta.numero) {
                                    minuta.movimentosCNJ = listaMM;
                                }
                            }
                        })
                    });
                });
            });
            runInAction(() => {
                ProcessoStore.carregandoMovimentosDeMinuta = false;
            });
        });
    }

    async recuperarMovimentosMinutas(processos: Processo[]) {
        const listasProcessos = chunk(processos, 900);
        const promises = listasProcessos.map(lista => MovimentoService.recuperarMovimentosMinutas(lista, false));
        const movimentosMinutasAux = (await Promise.all(promises)).flat(1);
        return movimentosMinutasAux;
    }

    async carregarErrosDeEmentaEDecisao(idsProcesso: number[] = []) {
        if (idsProcesso.length === 0) {
            idsProcesso = ProcessoStore._processos.map(proc => proc.identificadorProcesso);
        }

        let processos: Processo[] = ProcessoStore._processos.filter((processo) => processo.minutas.length > 0 && processo.minutas.find(minuta => minuta.tipo.codigo === TipoMinuta.VOTO));
        processos = processos.filter(proc => idsProcesso.includes(proc.identificadorProcesso));

        const ementasEDecisoes = await MinutaApi.verificaEmentaEDecisao(processos.map(proc => proc.identificadorProcesso));
        processos.forEach(proc => {
            const ementaDecisao: EmentaDecisao = ementasEDecisoes[proc.identificadorProcesso];
            let mensagemErro = "";
            const semDecisao = ementaDecisao.decisao?.length === 0 && ementaDecisao.dispositivo?.length === 0;
            if (semDecisao) {
                mensagemErro = "Não foi possível encontrar decisao";
            }

            if (!ementaDecisao.ementa || ementaDecisao.ementa.length === 0) {
                mensagemErro += semDecisao ? ", ementa." : "Não foi possível encontrar ementa.";
            }

            const voto = proc.minutas.find(minuta => minuta.tipo.codigo === TipoMinuta.VOTO);
            runInAction(() => {
                voto.semEmentaOuDecisao = mensagemErro;
            });
        })
    }
    async carregarMovimentosDeMinutasPorIdsProcessos(idsProcessos: number[]): Promise<void> {
        const processos: Processo[] = ProcessoStore._processos
            .filter((processo) => processo.minutas.length > 0 && idsProcessos.includes(processo.identificadorProcesso))

        if (processos.length === 0) {
            return;
        }

        const movimentosMinutas = await MovimentoService.recuperarMovimentosMinutas(processos, false);

        processos.forEach((processo) => {
            processo.minutas.forEach((minuta) => {
                runInAction(() => {
                    minuta.movimentosCNJ = movimentosMinutas[minuta.numero] || [];
                });
            });
        });
    }

    async carregarCategoriasDeMinutasDoProcesso(processo: Processo): Promise<void> {
        const numeros: number[] = [];

        processo.minutas.forEach((minuta) => {
            numeros.push(minuta.numero);
        });
        if (numeros.length === 0) {
            return;
        }

        const minutaComCategorias = await MinutaApi.recuperaCategoriaDasMinutas(numeros);
        processo.minutas.forEach((minuta) => {
            runInAction(() => {
                minuta.categorias = minutaComCategorias[minuta.numero] || [];
            });
        });

    }

    async carregarMovimentosDeMinuta(processo: Processo): Promise<void> {
        const processos: Processo[] = [processo];

        if (processos.length === 0) {
            return;
        }

        const movimentosMinutas = await MovimentoService.recuperarMovimentosMinutas(processos, false);
        processo.minutas.forEach((minuta) => {
            runInAction(() => {
                minuta.movimentosCNJ = movimentosMinutas[minuta.numero] || [];
            });
        });


    }

    consultaLiberacaoVoto = async (voto: Minuta) => {
        if (!voto || !voto?.idProcesso) {
            return
        }
        // let idVoto = await MinutaApi.consultarIdVoto(voto.idProcesso);
        const votoJaliberado = await MinutaApi.consultarLiberacaoConteudoVoto(voto.idProcesso);
        runInAction(() => {
            if (voto) {
                voto.publicavel = true
                voto.votoJaliberado = votoJaliberado as boolean;
            }
        });
    }

    async consultarIdVoto(idProcesso: number) {
        return await MinutaApi.consultarIdVoto(idProcesso);
    }

    liberarVotosEmLote = async (processos: Processo[]) => {
        return await MinutaApi.liberarVotosEmLote(processos.map(proc => proc.identificadorProcesso));
    }

    cancelarVotosEmLote = async (idsProcesso: number[]) => {
        return await MinutaApi.cancelarVotosEmLote(idsProcesso);
    }

    isTarefaIndevidaParaCriarMinuta(minuta: Minuta, processo: Processo) {
        try {
            // Apos alteração para utilizar a situação da minuta, estes controles não parecem ser mais necessários
            //let numeroOrdemVP = ControleGabineteStore.listaDeControles.find(controle => controle.isTipo(TipoControle.VISTOAPAUTA)).numeroOrdem;
            //let numeroOrdemAguardandoJulg = ControleGabineteStore.listaDeControles.find(controle => controle.isTipo(TipoControle.AGUARDANDO)).numeroOrdem;

            /*if ((minuta.isTipo(TipoMinuta.VOTO) && numeroOrdemVP === processo.controleGabinete.numeroOrdem)
                || (!minuta.isTipo(TipoMinuta.VOTO) && (numeroOrdemVP === processo.controleGabinete.numeroOrdem || numeroOrdemAguardandoJulg === processo.controleGabinete.numeroOrdem))) {
                    MensagemStore.apresentarInformacao('A minuta será criada na movimentação "Não distribuído".');
            }*/
        } catch (e) {
            console.error(e);
            console.info('info: controleGrabinete', processo.controleGabinete);
            if (!apresentarApiErroPadrao(e)) {
                MensagemStore.apresentarErro('Erro ao criar a minuta. Tente recarregar a página e fazer novamente.');
            }
        }
    }

    async visualizarMinutaAssinatura(numeroMinuta: number) {
        return await MinutaApi.recuperaMinutaParaAssinatura(numeroMinuta);
    }

    async visualizarMinutaAssinada(numeroMinuta: number) {
        return await MinutaApi.recuperaMinutaAssinada(numeroMinuta);
    }

    async visualizarVersaoMinuta(versao: VersaoMinutaDto) {
        return await MinutaApi.visualizarVersaoMinuta(versao);
    }

    async isEmEdicaoExterna(numero: number[]): Promise<IMinutaEmEdicaoExterna[]> {
        return await MinutaApi.isEmEdicaoExterna(numero)
    }

    estaRealmenteEmEdicaoExterna = async (numero: number) => {
        const resultado = await this.isEmEdicaoExterna([numero]);
        return resultado.length == 1 && resultado[0].emEdicaoExterna;
    }

    // async alterarEmEdicaoExterna(idProcesso: number, idTipoMinuta: number, emEdicaoExterna: boolean) {
    //     return await MinutaApi.alterarEmEdicaoExterna(idProcesso, idTipoMinuta, emEdicaoExterna);
    // }

    async listarHistoricoSituacaoMinuta(numMinuta: number): Promise<HistoricoSituacaoMinutaDto[]> {
        const historico = await MinutaApi.listarHistoricoSituacaoMinuta(numMinuta);
        return historico;
    }

    async listarHistoricoAlteracaoSituacaoMinuta(numMinutas: number[]): Promise<HistoricoMinutaDto[]> {
        const historico = await MinutaApi.listarHistoricoAlteracaoSituacaoMinuta(numMinutas, false);
        return historico;
    }

    async listarUltimoHistoricoSituacaoMinuta(numMinutas: number[]): Promise<HistoricoMinutaDto[]> {
        const historico = await MinutaApi.listarHistoricoAlteracaoSituacaoMinuta(numMinutas, true);
        return historico;
    }

    devolverParaSecretaria(minutas: Minuta[]) {
        MinutaApi.devolverEManterAssinado(minutas.map(minuta => minuta.numero)).then(() => {
            MensagemStore.apresentarMensagem("Minuta devolvida para secretaria");
        }).catch(error => {
            if (!apresentarApiErroPadrao(error)) {
                MensagemStore.apresentarErro("Não foi possível devolver minuta para secretaria");
            }
        });
    }

    async vincularPeticaoMinuta(numeroMinuta, peticoes) {
        const peticoesValidas = (peticoes || []).map((peticao) => ({
            idPeticao: peticao?.id ?? peticao?.identificadorDocumento ?? peticao?.numSeqPeca ?? peticao?.numPeticao,
            idUnico: peticao?.idUnicoDocumento ?? peticao?.idUnico
        })).filter(peticao => !!peticao.idPeticao && !!peticao.idUnico);

        if (peticoesValidas.length === 0) {
            MensagemStore.apresentarErro('Não foi possível identificar as petições selecionadas para vincular.');
            return;
        }

        try {
            return await Promise.all(peticoesValidas.map(peticao => {
                return MinutaApi.vincularPeticaoMinuta(numeroMinuta, peticao.idPeticao, peticao.idUnico);
            })).then(_result => {
                MensagemStore.apresentarMensagem("Vínculos efetuados com sucesso.");
            })
        } catch (error) {
            if (!apresentarApiErroPadrao(error)) {
                MensagemStore.apresentarErro("Não foi possível vincular minuta às petições. Favor tentar novamente, caso persista contate o administrador.");
            }
            console.error(error);
        }

    }

    async deletarVinculoPeticaoMinuta(numeroMinuta, peticao) {
        const idPeticao = peticao?.id ?? peticao?.identificadorDocumento ?? peticao?.numSeqPeca ?? peticao?.numPeticao;
        const idUnico = peticao?.idUnicoDocumento ?? peticao?.idUnico;

        if (!idPeticao || !idUnico) {
            MensagemStore.apresentarErro('Não foi possível identificar o vínculo da petição para remover.');
            return;
        }

        try {
            return await MinutaApi.deletarVinculoPeticaoMinuta(numeroMinuta, idPeticao, idUnico).then(_r => {
                MensagemStore.apresentarMensagem("Vínculo removido com sucesso.");
            });
        } catch (error) {
            if (!apresentarApiErroPadrao(error)) {
                MensagemStore.apresentarErro("Não foi possível deletar o vínculo da minuta à petição. Favor tentar novamente, caso persista contate o administrador.");
            }
            console.error(error);
        }
    }

    async listarVinculosPeticaoMinuta(numeroMinuta) {
        return await MinutaApi.listarVinculosPeticaoMinuta(numeroMinuta);
    }

    async consultarMinutasArquivadas(idProcesso: number) {
        const minutasOrdenadas = [];
        const minutas = await MinutaApi.recuperaMinutasArquivadas(idProcesso).catch(
            (err) => new Error(err)
        );

        (minutas as Minuta[]).forEach((minuta) => {
            minutasOrdenadas.push(Minuta.criar(minuta));
        });
        minutasOrdenadas.sort((m1, m2) => {
            if (m1.dataUltimaAlteracao > m2.dataUltimaAlteracao) {
                return 1;
            } else {
                return -1;
            }
        });

        return minutasOrdenadas;
    }

    async consultarMinutasAssinadas(idProcesso: number) {
        const minutasOrdenadas = [];
        const minutas = await MinutaApi.recuperaMinutasAssinadas(idProcesso).catch(
            (err) => new Error(err)
        );
        (minutas as Minuta[]).forEach((minuta) => {
            minutasOrdenadas.push(Minuta.criar(minuta));
        });
        minutasOrdenadas.sort((m1, m2) => {
            if (m1.dataUltimaAlteracao > m2.dataUltimaAlteracao) {
                return 1;
            } else {
                return -1;
            }
        });

        return minutasOrdenadas;
    }

    async listarVersoesMinuta(numeroMinuta: number): Promise<VersaoMinutaDto[]> {
        let versoes = await MinutaApi.listarVersoesMinuta(numeroMinuta);
        versoes = versoes.sort(function (a, b) {
            // Turn your strings into dates, and then subtract them
            // to get a value that is either negative, positive, or zero.
            return moment(b.ultimaAlteracao).toDate().getTime() - moment(a.ultimaAlteracao).toDate().getTime();
        });
        return versoes;
    }


    async baixarVersaoMinuta(versaoMinuta: VersaoMinutaDto, numeroMinuta: number, numeroProcesso: string) {
        const blob = await MinutaApi.baixarVersaoMinuta(versaoMinuta.docId, versaoMinuta.tipo, versaoMinuta.extensao);
        const newBlob = new Blob([blob], { type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document" })
        if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
            (window.navigator as any).msSaveOrOpenBlob(newBlob);
            return;
        }
        const data = window.URL.createObjectURL(newBlob);
        const link = document.createElement('a');
        link.href = data;
        const nome = numeroProcesso + '-' + numeroMinuta
        link.download = nome + ".docx";
        link.click();
        setTimeout(function () {
            window.URL.revokeObjectURL(data);
        }, 100);

    }

    async restaurarVersaoMinuta(versaoMinuta: VersaoMinutaDto, numeroMinuta: number, _numeroProcesso: string) {
        const minuta = await MinutaApi.restaurarVersaoMinuta(numeroMinuta, versaoMinuta.docId, versaoMinuta.tipo, versaoMinuta.extensao).then(() => {
            MensagemStore.apresentarMensagem('Versão anterior da minuta restaurada');
        });
        return minuta;
    }

    async gerarEmentaAguia(numMinuta: number, estiloEmenta: string): Promise<any> {
        return await MinutaApi.gerarEmentaAguia(numMinuta, estiloEmenta);
    }

    async salvarEmentaGeradaAguia(numMinuta: number, docx: string) {
        try {
            await MinutaApi.salvarEmentaGeradaAguia(numMinuta, docx);
        } catch (error) {
            console.log(error)
        } finally {
            MensagemStore.apresentarMensagem("Ementa gerada foi adicionada à minuta.")
        }
    }

    async restaurarMinutaArquivada(minuta: Minuta) {
        await MinutaApi.restaurarMinutaArquivada(minuta.numero).then(() => {
            MensagemStore.apresentarMensagem('Minuta restaurada');
        });
        return;
    }
}

export default new MinutaService();
